// Query-key factory for the portal-absorption spine (the shared per-party
// portal-management components). Lives in its own *Keys file per the
// stale-after-save convention. Per-type invite/disable mutations must
// invalidate `all` so the PortalAccountCard refreshes after an action.
// NOTE: the review-queue + needs-attention keys deliberately live in
// api/collectionStagingKeys.ts under the "collection-staging" prefix so the
// shared review drawer's mutations refresh them for free.

export const portalAbsorptionKeys = {
  all: ["portal-absorption"] as const,
  accountDetail: (portalUserId: string) =>
    ["portal-absorption", "account-detail", portalUserId] as const,
  // WS-G — the account's admin activity feed (Portal Account tab timeline).
  accountActivity: (portalUserId: string) =>
    ["portal-absorption", "account-activity", portalUserId] as const,
  accountNotifications: (portalUserId: string) =>
    ["portal-absorption", "account-notifications", portalUserId] as const,
  // WS-E — Review & requests tab + New-document-request modal reads.
  // accountPackages lives under the "portal-account-packages" prefix so the
  // composer page's existing prefix invalidation reaches it; composerTemplates/
  // composerBundles share the composer page's cache entries (same key shape +
  // same list(false, audience) queryFn).
  accountPackagesPrefix: ["portal-account-packages"] as const,
  accountPackages: (portalUserId: string) =>
    ["portal-account-packages", portalUserId, "review-requests"] as const,
  // F8 — the item list for one account package, read on demand when the
  // "request view" slide-out opens. Under the accountPackages prefix so a
  // reminder / due-date edit's prefix invalidation refreshes it for free.
  // WS-F — keyed by includeArchived so the "Show archived" toggle re-fetches;
  // both variants ride the accountPackagesPrefix invalidation.
  accountPackageItems: (
    portalUserId: string,
    packageId: string,
    includeArchived: boolean,
  ) =>
    ["portal-account-packages", portalUserId, "items", packageId, includeArchived] as const,
  composerTemplates: (audience: string | null) =>
    ["collection-templates", "active", audience] as const,
  // Owner Recapitalization Loan (ruling Q8) — a list read for a deal (recap
  // bundles hidden or suggested by deal type) is its own entry; without a deal
  // the key is unchanged, so it still shares the composer page's entry.
  composerBundles: (audience: string | null, dealId: string | null = null) =>
    dealId
      ? (["collection-bundles", "active", audience, "deal", dealId] as const)
      : (["collection-bundles", "active", audience] as const),
  // F4 — Step-2 server-resolved merge-field prefill (one entry per selected
  // special-form set + party context). Read-only definition data; no
  // mutation invalidates it (template edits are rare and staleTime-bounded).
  composerSpecialFormDefinitions: (
    idsKey: string,
    partyId: string | null,
    dealId: string | null = null,
  ) => ["portal-absorption", "special-form-definitions", idsKey, partyId, dealId] as const,
};
